import { Box, Divider, Tooltip, Typography } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import * as React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { countries } from "../../utils/countries";

const commonCountries = ["FI", "US", "SE", "NO", "CA", "ES", "GB"];
const commonCurrencies = ["EU", "US", "SE", "NO", "CA", "GB"];

interface GroupedCountriesProps {
  country: string;
  handleCountryChange: (
    event: React.SyntheticEvent,
    newValue: string | null,
    newCurrency: string | null
  ) => void;
}

interface GroupedCurrenciesProps {
  currency: string;
  handleCurrencyChange: (
    event: React.SyntheticEvent,
    newCurrency: string | null
  ) => void;
}

export default function GroupedCountries(props: GroupedCountriesProps) {
  const { country, handleCountryChange } = props;

  const [selectedCountry, setSelectedCountry] = React.useState(country);

  const intl = useIntl();

  countries.sort((a, b) => {
    const aIsPreferred = commonCountries.includes(a.code);
    const bIsPreferred = commonCountries.includes(b.code);

    if (aIsPreferred && !bIsPreferred) return -1;
    if (!aIsPreferred && bIsPreferred) return 1;

    return a.label.localeCompare(b.label);
  });

  const structuredOptions = countries.map((option) => ({
    ...option,
    group: commonCountries.includes(option.code) ? "Preferred" : "Others",
  }));

  const selectedOption = structuredOptions.find(
    (option) => option.label === country
  );

  return (
    <Autocomplete
      id="autocomplete-country"
      options={structuredOptions}
      value={selectedOption || structuredOptions[1]}
      clearOnBlur={false}
      onBlur={() => setSelectedCountry(selectedCountry)}
      onChange={(event, option) => {
        if (!option) {
          return;
        }
        const selectedCountry = countries.find(
          (country) => country.label === option?.label
        );
        if (selectedCountry) {
          handleCountryChange(
            event,
            selectedCountry.label,
            selectedCountry.currencyCode
          );
          setSelectedCountry(option.label);
        }
      }}
      groupBy={(option) => option.group}
      getOptionLabel={(option) => option?.label || selectedCountry}
      getOptionKey={(option) => option.code}
      autoHighlight={true}
      renderGroup={(params) => (
        <div key={params.key}>
          {params.group === "Preferred" && (
            <Divider>Frequently Selected</Divider>
          )}
          {params.group === "Others" && <Divider>All Countries</Divider>}
          {params.children}
        </div>
      )}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <Box
            key={option.code}
            id="country-box"
            component="li"
            sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
            {...optionProps}
          >
            <img
              loading="lazy"
              width="20"
              srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
              src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
              alt=""
            />
            {option.label}
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          inputProps={{
            ...params.inputProps,
            autoComplete: "new-password",
          }}
          placeholder={intl.formatMessage({
            id: "country",
            defaultMessage: "Select a country",
          })}
          label={intl.formatMessage({
            id: "country",
            defaultMessage: "Countries",
          })}
          name="country"
        />
      )}
    />
  );
}

export function GroupedCurrencies(props: GroupedCurrenciesProps) {
  const { currency, handleCurrencyChange } = props;

  const [selectedCurrency, setSelectedCurrency] = React.useState(currency);

  const intl = useIntl();

  countries.sort((a, b) => {
    const aIsPreferred = commonCurrencies.includes(a.code);
    const bIsPreferred = commonCurrencies.includes(b.code);

    if (aIsPreferred && !bIsPreferred) return -1;
    if (!aIsPreferred && bIsPreferred) return 1;

    return a.label.localeCompare(b.label);
  });

  const structuredOptions = countries.map((option) => ({
    ...option,
    group: commonCurrencies.includes(option.code) ? "Preferred" : "Others",
  }));

  const selectedOption = structuredOptions.find(
    (option) => option.currencyCode === currency
  );

  return (
    <Autocomplete
      id="autocomplete-currency"
      options={structuredOptions}
      groupBy={(option) => option.group}
      getOptionLabel={(option) =>
        option.currencyCode + ", " + option?.currencySymbol || selectedCurrency
      }
      getOptionKey={(option) => option.code}
      value={selectedOption || structuredOptions[1]}
      clearOnBlur={false}
      onChange={(event, option) => {
        if (!option) {
          return;
        }
        handleCurrencyChange(event, option.currencyCode);
        setSelectedCurrency(option?.currencyCode);
      }}
      autoHighlight={true}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <Box
            id="currencyBox"
            key={option.label}
            component="li"
            sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
            {...optionProps}
          >
            <img
              loading="lazy"
              width="20"
              srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
              src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
              alt=""
            />
            {option.currencySymbol} ({option.currencyCode})
          </Box>
        );
      }}
      renderInput={(params) => (
        <Tooltip
          title={
            <Typography variant="body1">
              <FormattedMessage
                id="currency-tip"
                defaultMessage={
                  "Automatically set by country. Click to override"
                }
              />
            </Typography>
          }
        >
          <TextField
            {...params}
            inputProps={{
              ...params.inputProps,
              autoComplete: "new-password",
            }}
            name="currency"
            label={intl.formatMessage({
              id: "currency",
              defaultMessage: "Currency",
            })}
          />
        </Tooltip>
      )}
      renderGroup={(params) => (
        <div key={params.key}>
          {params.group === "Preferred" && <Divider>Common Currency</Divider>}
          {params.group === "Others" && <Divider>All Currency</Divider>}
          {params.children}
        </div>
      )}
    />
  );
}
